import React, { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  getCommonTextFilterParams,
  getSetFilterParams,
  getSetDatefilterParams,
} from '../../utils/agGridFilters';
import { isMobile } from '../../utils/deviceUtils';
import DeleteConfirmationModal from '../../shared/modal/deleteConfirmation';
import { DateFormatter, DateTimeFormatter, NameFormatter } from '../../shared/controls/formatters';

const OptionButton = ({
  handleOpenEditForm,
  paramsValue,
  handleOpenViewForm,
  rowData,
  handleClickDelete,
  hasDeleteTicketPermission,
  hasUpdateTicketPermission,
  hasInfoTicketPermission,
}) => {
  const [showConfirmation, setShowConfirmation] = React.useState(false);

  const handleDeleteClick = () => {
    setShowConfirmation(true);
  };

  const handleConfirmDelete = () => {
    handleClickDelete(paramsValue);
    setShowConfirmation(false);
  };

  const handleCloseConfirmation = () => {
    setShowConfirmation(false);
  };

  return (
    <div className="action-menu">
      {hasUpdateTicketPermission && (
        <img
          src="/assets/images/edit.png"
          className="action-img1"
          onClick={() => handleOpenEditForm(paramsValue)}
          alt="Edit Ticket"
          title="Edit Ticket"
        ></img>
      )}

      {hasInfoTicketPermission && (
        <img
          src="/assets/images/view.png"
          className="action-img2"
          onClick={() => handleOpenViewForm(paramsValue)}
          alt="View Ticket"
          title="View Ticket"
        ></img>
      )}

      {hasDeleteTicketPermission && (
        <img
          src="/assets/images/trash.png"
          className="action-img2"
          onClick={handleDeleteClick}
          alt="Delete Ticket"
          title="Delete Ticket"
        ></img>
      )}
      <DeleteConfirmationModal
        show={showConfirmation}
        handleClose={handleCloseConfirmation}
        handleConfirm={handleConfirmDelete}
        rowData={rowData}
      />
    </div>
  );
};

const useTaskColumnDefs = ({
  hasUpdateTicketPermission,
  hasInfoTicketPermission,
  hasDeleteTicketPermission,
  ticketStatusDropdown,
  ticketPriorityDropdown,
  owner,
  handleClickDelete,
}) => {
  const navigate = useNavigate();
  const commonTextFilterParams = useMemo(() => getCommonTextFilterParams(), []);
  const dateFilterParams = useMemo(() => getSetDatefilterParams(), []);
  const filterParams = useMemo(() => getSetFilterParams, []);
  const handleOpenEditForm = (paramsValue) => {
    navigate('/tickets/editTicket', {
      state: paramsValue,
    });
  };

  const handleOpenViewForm = (paramsValue) => {
    navigate(`/tickets/viewTicket?id=${paramsValue?.id}`, {
      state: paramsValue,
    });
  };
  const actionsColumn = useMemo(() => {
    if (!(hasUpdateTicketPermission || hasInfoTicketPermission || hasDeleteTicketPermission))
      return [];

    return [
      {
        headerName: 'Actions',
        headerCheckboxSelection: (params) => params?.node && !params.node.group,
        headerCheckboxSelectionFilteredOnly: true,
        checkboxSelection: (params) => params?.node && !params.node.group,
        cellRenderer: (params) => {
          if (!params?.node || params.node.group) return null;
          const rowData = { ...params.data, rowIndex: params.rowIndex };
          return (
            <OptionButton
              rowData={rowData}
              hasInfoTicketPermission={hasInfoTicketPermission}
              hasDeleteTicketPermission={hasDeleteTicketPermission}
              hasUpdateTicketPermission={hasUpdateTicketPermission}
              handleOpenEditForm={() => handleOpenEditForm(rowData)}
              handleOpenViewForm={() => handleOpenViewForm(rowData)}
              handleClickDelete={() => handleClickDelete(rowData)}
            />
          );
        },
        maxWidth: 100,
        minWidth: 100,
        sortable: false,
        filter: false,
        pinned: 'left',
      },
    ];
  }, [
    hasUpdateTicketPermission,
    hasInfoTicketPermission,
    hasDeleteTicketPermission,
    handleOpenEditForm,
    handleOpenViewForm,
    handleClickDelete,
  ]);

  const columnDefs = useMemo(() => {
    if (!owner?.length || !ticketStatusDropdown?.length || !ticketPriorityDropdown?.length)
      return [];

    return [
      ...actionsColumn,
      {
        headerName: 'Subject',
        field: 'subject',
        pinned: isMobile() ? null : 'left',
        minWidth: 180,
        cellRenderer: (params) => {
          if (!params.value) return null;
          return (
            <Link
              to={`/tickets/viewTicket?id=${params?.data?.id}`}
              state={params?.data}
              style={{ fontWeight: '600' }}
            >
              {params.value}
            </Link>
          );
        },
        filter: 'agTextColumnFilter',
        filterParams: commonTextFilterParams,
      },
      {
        headerName: 'Status',
        field: 'status',
        pinned: isMobile() ? null : 'left',
        filter: 'agSetColumnFilter',
        filterParams: filterParams(ticketStatusDropdown?.map((item) => item.displayName)),
      },
      {
        headerName: 'Priority',
        field: 'priority',
        pinned: isMobile() ? null : 'left',
        filter: 'agSetColumnFilter',
        filterParams: filterParams(ticketPriorityDropdown?.map((item) => item.displayName)),
      },
      {
        headerName: 'Owner',
        field: 'owner',
        // pinned: isMobile() ? null : "left",
        minWidth: 180,
        filter: 'agSetColumnFilter',
        filterParams: filterParams(owner?.map((item) => item.display_name)),
      },
      {
        headerName: 'Lead Name',
        field: 'leadName',
        filter: 'agTextColumnFilter',
        filterParams: commonTextFilterParams,
      },
      {
        headerName: 'Contact Name',
        field: 'contactName',
        filter: 'agTextColumnFilter',
        filterParams: commonTextFilterParams,
      },
      {
        headerName: 'Account Name',
        field: 'accountName',
        filter: 'agTextColumnFilter',
        filterParams: commonTextFilterParams,
      },
      {
        headerName: 'Opportunity Name',
        field: 'opportunityName',
        filter: 'agTextColumnFilter',
        filterParams: commonTextFilterParams,
      },
      {
        headerName: 'Campaign Name',
        field: 'campaignName',
        filter: 'agTextColumnFilter',
        filterParams: commonTextFilterParams,
      },
      {
        headerName: 'Due Date',
        field: 'dueDate',
        valueFormatter: DateFormatter,
        minWidth: 230,
        filter: 'agDateColumnFilter',
        filterParams: dateFilterParams,
      },

      {
        headerName: 'Reminder',
        field: 'reminder',
        filter: 'agDateColumnFilter',
        valueFormatter: DateTimeFormatter,
        filterParams: dateFilterParams,
      },
      {
        headerName: 'Description',
        field: 'description',
        filter: 'agTextColumnFilter',
        filterParams: commonTextFilterParams,
      },
      {
        headerName: 'Created By',
        field: 'createdBy',
        filter: 'agTextColumnFilter',
        filterParams: commonTextFilterParams,
        valueFormatter: NameFormatter,
      },
      {
        headerName: 'Updated By',
        field: 'updatedBy',
        filter: 'agTextColumnFilter',
        filterParams: commonTextFilterParams,
        valueFormatter: NameFormatter,
      },
      {
        headerName: 'Created Date Time',
        field: 'createdDateTime',
        valueFormatter: DateTimeFormatter,
        filter: 'agDateColumnFilter',
        filterParams: dateFilterParams,
      },
      {
        headerName: 'Updated Date Time',
        field: 'updatedDateTime',
        valueFormatter: DateTimeFormatter,
        filter: 'agDateColumnFilter',
        filterParams: dateFilterParams,
      },
    ];
  }, [
    hasInfoTicketPermission,
    owner,
    ticketPriorityDropdown,
    ticketStatusDropdown,
    actionsColumn,
    commonTextFilterParams,
    filterParams,
  ]);

  return columnDefs;
};

export default useTaskColumnDefs;
